import { useState } from 'react';
import { findReaction } from '@campus/core';
import { sessionInfo } from '../ui/people/membersStore.js';

/**
 * Emojis les plus utilisés par la personne (2026-11-03) : seconde ligne sous
 * les réactions de base, partout où il y en a (menu Réagir du campus, menu
 * d'un message, emojis de la saisie). Compté dans ce navigateur, par compte et
 * par campus : réactions envoyées sur le campus, réactions aux messages,
 * emojis insérés dans un message. Rien n'est envoyé au serveur.
 */

interface Usage { n: number; at: number }
type UsageMap = Record<string, Usage>;

const MAX_TRACKED = 40;

/** Complètent la ligne tant que la personne n'a pas assez d'habitudes (nouveau compte). */
const DEFAULT_FILL = ['fire', 'rocket', '100', 'smile', 'thinking', 'wow', 'raised-hands', 'ok', 'eyes', 'sad', 'clap', 'thanks', 'check', 'wave'];

function key(): string | null {
  const { tenantId, userId } = sessionInfo();
  return userId ? `placio:reaction-usage:${tenantId}:${userId}` : null;
}

function read(): UsageMap {
  const k = key();
  if (!k) return {};
  try {
    const raw = JSON.parse(localStorage.getItem(k) ?? '{}') as unknown;
    return raw && typeof raw === 'object' ? (raw as UsageMap) : {};
  } catch {
    return {};
  }
}

/** Une utilisation de plus pour cet emoji (identifiant du catalogue). */
export function recordReactionUse(id: string): void {
  const k = key();
  if (!k || !findReaction(id)) return;
  const usage = read();
  const prev = usage[id];
  usage[id] = { n: (prev?.n ?? 0) + 1, at: Date.now() };
  // Garder les plus utilisés seulement.
  const kept = Object.entries(usage).sort(([, a], [, b]) => b.n - a.n || b.at - a.at).slice(0, MAX_TRACKED);
  try { localStorage.setItem(k, JSON.stringify(Object.fromEntries(kept))); } catch { /* stockage indisponible */ }
}

/**
 * Les plus utilisés (puis les plus récents), hors `exclude`, `count` au plus ;
 * complétés par des emojis courants tant que l'historique est court.
 */
export function mostUsedReactions(exclude: readonly string[], count: number): string[] {
  const skip = new Set(exclude);
  const used = Object.entries(read())
    .filter(([id]) => !skip.has(id) && !!findReaction(id))
    .sort(([, a], [, b]) => b.n - a.n || b.at - a.at)
    .map(([id]) => id);
  for (const id of DEFAULT_FILL) if (!skip.has(id) && !used.includes(id) && findReaction(id)) used.push(id);
  return used.slice(0, count);
}

/**
 * Lu à l'ouverture du menu (monté à chaque ouverture) puis figé : la ligne ne
 * bouge pas sous la souris pendant qu'on enchaîne les réactions.
 */
export function useMostUsedReactions(exclude: readonly string[], count: number): string[] {
  const [ids] = useState(() => mostUsedReactions(exclude, count));
  return ids;
}
